import React, { memo, useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Portal } from 'utils/packages/portal';
import Button from 'design-system/Button';
import ModalSheet from 'design-system/ModalSheet';
import { IconCloseSquircle, IconSquareCircle } from 'design-system/icons';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useIsTablet from 'hooks/useIsTablet';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { screensToAvoidRenderingTopOf } from 'utils/types/navigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setShouldSeeDeviceRootedWarningModal } from 'actions/sessionSettings';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { noop } from 'lodash';
import ModalHandle from 'design-system/ModalHandle';
import { rem } from 'design-system/values';
export const DEVICE_ROOTED_KEY = 'DEVICE_ROOTED_WARNING_SHOWN';
const RootedDeviceWarningModal = () => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const currentScreen = useAppSelector((state) => state.utils.currentScreen);
    const styles = useStyles(styleSet);
    const shouldSeeDeviceRootedWarningModal = useAppSelector((state) => state.sessionSettings.shouldSeeDeviceRootedWarningModal);
    const containerStyles = useMarginBottom('paddingBottom', 0, styles.container);
    const isTablet = useIsTablet();
    const { width } = useAppFrameDimensions();
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (shouldSeeDeviceRootedWarningModal) {
            let canShow = true;
            navigation.getState()?.routes.forEach((routeObj) => {
                if (screensToAvoidRenderingTopOf[routeObj.name]) {
                    canShow = false;
                }
            });
            if (canShow) {
                setVisible(true);
            }
        }
        else {
            setVisible(false);
        }
    }, [shouldSeeDeviceRootedWarningModal, currentScreen]);
    const closeScreen = useCallback(() => {
        AsyncStorage.setItem(DEVICE_ROOTED_KEY, 'true');
        dispatch(setShouldSeeDeviceRootedWarningModal(false));
    }, []);
    let card = (<View style={isTablet
            ? [styles.container, staticStyles.containerTablet]
            : containerStyles}>
      <ModalHandle />
      <IconSquareCircle style={styles.icon} width={72} height={72} color={colors.background.dark}>
        <IconCloseSquircle width={40} height={40}/>
      </IconSquareCircle>
      <Text Text-20 OffBlack style={styles.title}>
        Your device is rooted
      </Text>
      <Text TextThin-16 Gray centered style={styles.subtitle}>
        This app is running on a rooted system. The security of your account
        might be compromised by third parties. We strongly suggest to use Emma
        on non rooted devices.
      </Text>
      <Button gray style={styles.button} title="Don’t show again" onPress={closeScreen}/>
    </View>);
    // On a tablet the card sits in the middle of a full-height, transparent
    // sheet, like design-system/Modal's. A press around it does nothing, as a
    // backdrop press does.
    if (isTablet) {
        card = (<View style={staticStyles.tabletCentre}>
        <Pressable style={StyleSheet.absoluteFill} onPress={noop} accessible={false}/>
        <View style={{ width }}>{card}</View>
      </View>);
    }
    return (
    // In a portal, so it covers the screen and any open Modal.
    <Portal>
      <ModalSheet visible={visible} fullHeight={isTablet} dismissable onBackdropPress={noop} onSwipeComplete={closeScreen} onBackButtonPress={noop} onHide={noop}>
        {card}
      </ModalSheet>
    </Portal>);
};
const staticStyles = StyleSheet.create({
    // design-system/Modal's tablet card, centred in a full-height sheet.
    tabletCentre: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    containerTablet: {
        borderRadius: rem(16),
        paddingBottom: rem(16),
    },
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(50),
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    icon: {
        marginBottom: rem(24),
    },
    title: {
        marginBottom: rem(4),
    },
    subtitle: {
        marginBottom: rem(32),
    },
    button: {
        alignSelf: 'stretch',
    },
}));
export default memo(RootedDeviceWarningModal);
